import { useEffect, useState } from "react";
import { FONT, COLOR, CHIP_TONES } from "../lib/tokens";

// Single +N XP toast. Top-right slide-in, auto-dismisses after
// `holdMs` (default 2000ms). Color pulled from the reserved
// palette (CLAUDE.md identity hierarchy) when the source maps
// to one of the six axes; otherwise a neutral muted gold.
//
// Per §5: "Toast sits for ~2s, then slides out." Single line,
// no decoration beyond the +N value and a short copy.
//
// Source → palette map:
//   canonical_create / canonical_approved → tan    (#b8a878 — CANONICAL)
//   scan_add / pantry_hygiene             → blue   (#7eb8d4 — STORED IN)
//   review_cook                           → muted purple (STATE-ish)
//   photo_upload                          → muted gold
//   nutrition_goal_day                    → yellow (#f5c842)
//   onboarding_*                          → yellow
//   badge_earn                            → yellow
//   plan_cook_closed / eat_together /
//   cook_together / authors_cut /
//   first_time_recipe / mastery_*         → orange (#e07a3a — FOOD CATEGORY)
//   default                               → gold (#d4a87a)

const SOURCE_PALETTE = {
  canonical_create:    { color: CHIP_TONES.canonical.fg, label: "Canonical submitted" },
  canonical_approved:  { color: CHIP_TONES.canonical.fg, label: "Canonical approved" },
  scan_add:            { color: COLOR.sky, label: "Pantry add" },
  pantry_hygiene:      { color: COLOR.sky, label: "Pantry hygiene" },
  review_cook:         { color: COLOR.plum, label: "Review posted" },
  photo_upload:        { color: CHIP_TONES.canonical.fg, label: "Photo added" },
  nutrition_goal_day:  { color: COLOR.gold, label: "Nutrition goal" },
  badge_earn:          { color: COLOR.gold, label: "Badge earned" },
  plan_cook_closed:    { color: CHIP_TONES.category.fg, label: "Plan closed" },
  eat_together:        { color: CHIP_TONES.category.fg, label: "Eaten together" },
  cook_together:       { color: CHIP_TONES.category.fg, label: "Cook-together" },
  authors_cut:         { color: CHIP_TONES.category.fg, label: "Author's cut" },
  first_time_recipe:   { color: CHIP_TONES.category.fg, label: "First-time cook" },
  mastery_5x:          { color: CHIP_TONES.category.fg, label: "Mastery · 5×" },
  mastery_10x:         { color: CHIP_TONES.category.fg, label: "Mastery · 10×" },
  mastery_25x:         { color: CHIP_TONES.category.fg, label: "Mastery · 25×" },
  authored_recipe:     { color: COLOR.gold, label: "Recipe authored" },
  cook_complete:       { color: CHIP_TONES.canonical.fg, label: "Cook complete" },
  daily_roll:          { color: COLOR.gold, label: "Daily roll" },
};

function paletteFor(row) {
  if (row.source && row.source.startsWith("onboarding_")) {
    return { color: COLOR.gold, label: "First time!" };
  }
  return SOURCE_PALETTE[row.source] || { color: CHIP_TONES.canonical.fg, label: row.source };
}

export default function XpToast({ row, onDismiss, holdMs = 2000 }) {
  const [phase, setPhase] = useState("entering");
  const palette = paletteFor(row);
  const isGated = (row.gate_adjustment || 0) < 0;
  const value   = isGated ? row.gate_adjustment : row.final_xp;

  useEffect(() => {
    const enterT = setTimeout(() => setPhase("holding"), 50);
    const exitT  = setTimeout(() => setPhase("leaving"), holdMs);
    const doneT  = setTimeout(() => onDismiss?.(row.id), holdMs + 300);
    return () => {
      clearTimeout(enterT);
      clearTimeout(exitT);
      clearTimeout(doneT);
    };
  }, [row.id, holdMs, onDismiss]);

  const translate = phase === "entering" ? "translateX(120%)"
                  : phase === "leaving"  ? "translateX(120%)"
                  : "translateX(0)";
  const opacity   = phase === "entering" || phase === "leaving" ? 0 : 1;

  return (
    <div style={{
      background: COLOR.soil,
      border: `1px solid ${palette.color}33`,
      borderLeft: `3px solid ${palette.color}`,
      borderRadius: 10,
      padding: "10px 14px",
      minWidth: 190,
      maxWidth: 260,
      boxShadow: "0 4px 18px rgba(0,0,0,.4)",
      transform: translate,
      opacity,
      transition: "transform 280ms ease-out, opacity 240ms ease-out",
      display: "flex",
      alignItems: "baseline",
      gap: 10,
    }}>
      <span style={{
        fontFamily: FONT.mono,
        fontSize: 18,
        fontWeight: 600,
        color: isGated ? COLOR.dim : palette.color,
        whiteSpace: "nowrap",
      }}>
        {value > 0 ? "+" : ""}{value} XP
      </span>
      <span style={{
        fontFamily: FONT.sans,
        fontSize: 12,
        color: COLOR.dim,
        textTransform: "lowercase",
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}>
        {isGated ? "blocked by gate" : palette.label}
      </span>
    </div>
  );
}
